import { Component, ElementRef, Injector, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { FormControl } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { switchMap } from 'rxjs/operators';
import { MfaService, MfaSetupInitiateResponse } from '../../../../../aumico-lib-users-fe/src/lib/services/mfa.service';
import { Paths } from '../../enums/paths.enum';
import { BasePageComponent } from '../base.page';

@UntilDestroy()
@Component({
    selector: 'app-page-mfa-setup',
    templateUrl: './mfa-setup.page.html',
    styleUrls: ['./mfa-setup.page.scss'],
})
export class MfaSetupPage extends BasePageComponent implements OnInit, OnDestroy {
    currentStep: 1 | 2 | 3 = 1;
    setupData: MfaSetupInitiateResponse | null = null;
    backupCodes: string[] = [];
    isLoading = false;
    errorMessage: string | null = null;

    otpControls = Array.from({ length: 6 }, () => new FormControl(''));
    @ViewChildren('otpInput') otpInputs!: QueryList<ElementRef>;

    get isOtpComplete(): boolean {
        return this.otpControls.every(c => (c.value as string).length === 1);
    }

    get otpCode(): string {
        return this.otpControls.map(c => c.value as string).join('');
    }

    constructor(injector: Injector, private mfaService: MfaService) {
        super(injector);
    }

    override ngOnInit(): void {
        this.startSetup();
    }

    private startSetup(): void {
        this.isLoading = true;
        this.errorMessage = null;
        this.mfaService.setupInitiate$()
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (data) => {
                    this.setupData = data;
                    this.isLoading = false;
                },
                error: () => {
                    this.isLoading = false;
                    this.errorMessage = 'aumico_mfa_setup_error_initiate';
                },
            });
    }

    goBack(): void {
        if (this.currentStep === 2) {
            this.currentStep = 1;
            this.errorMessage = null;
            this.otpControls.forEach(c => c.setValue(''));
        } else {
            window.history.back();
        }
    }

    goToVerifyStep(): void {
        this.errorMessage = null;
        this.otpControls.forEach(c => c.setValue(''));
        this.currentStep = 2;
        setTimeout(() => this.otpInputs.first?.nativeElement.focus(), 50);
    }

    onOtpInput(index: number, event: Event): void {
        const input = event.target as HTMLInputElement;
        const val = input.value.replace(/\D/g, '').slice(-1);
        this.otpControls[index].setValue(val);
        input.value = val;
        if (val && index < 5) {
            this.otpInputs.toArray()[index + 1]?.nativeElement.focus();
        }
    }

    onOtpKeydown(index: number, event: KeyboardEvent): void {
        if (event.key === 'Backspace' && !(this.otpControls[index].value as string) && index > 0) {
            this.otpControls[index - 1].setValue('');
            this.otpInputs.toArray()[index - 1]?.nativeElement.focus();
        }
    }

    onOtpPaste(event: ClipboardEvent): void {
        event.preventDefault();
        const digits = (event.clipboardData?.getData('text') ?? '').replace(/\D/g, '').slice(0, 6);
        digits.split('').forEach((d, i) => this.otpControls[i]?.setValue(d));
        const inputs = this.otpInputs.toArray();
        digits.split('').forEach((d, i) => { if (inputs[i]) inputs[i].nativeElement.value = d; });
        inputs[Math.min(digits.length, 5)]?.nativeElement.focus();
    }

    onVerify(): void {
        const code = this.otpCode;
        if (code.length !== 6) return;
        this.isLoading = true;
        this.errorMessage = null;
        this.mfaService.setupVerify$(code)
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (result) => {
                    this.backupCodes = result.backupCodes;
                    this.isLoading = false;
                    this.currentStep = 3;
                },
                error: () => {
                    this.isLoading = false;
                    this.errorMessage = 'aumico_mfa_setup_error_invalid_code';
                    this.otpControls.forEach(c => c.setValue(''));
                    setTimeout(() => this.otpInputs.first?.nativeElement.focus(), 50);
                },
            });
    }

    copyToClipboard(code: string): void {
        navigator.clipboard.writeText(code).catch(() => undefined);
    }

    downloadBackupCodes(): void {
        const content = this.backupCodes.join('\n');
        const blob = new Blob([content], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'aumico-backup-codes.txt';
        a.click();
        URL.revokeObjectURL(url);
    }

    onContinue(): void {
        this.mfaService.forceTokenRefresh$()
            .pipe(
                switchMap(() => {
                    this.authProvider.dispatchGetCurrentUser();
                    return this.authProvider.getCurrentUser$();
                }),
                untilDestroyed(this),
            )
            .subscribe((user) => {
                if (user) {
                    const returnUrl = this.safeReturnUrl();
                    if (returnUrl != null) {
                        void this.router.navigateByUrl(returnUrl);
                        return;
                    }
                    const lang = this.getCurrentLanguageWithFallBack();
                    this.router.navigate([`/${lang}/${Paths.HOME}`]);
                }
            });
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();
    }
}
